import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, CheckSquare, Users, UserCheck, LogOut, Wrench, Activity, BarChart3, FileSpreadsheet, CalendarDays, Receipt, Menu, X, KeyRound, FileText, ArrowLeftRight, Keyboard, BriefcaseBusiness, FolderOpen, Inbox, Clock, Target, Gauge } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import NotificationBell from './NotificationBell';
import api from '../utils/api';
import { toast } from 'sonner';

export default function Sidebar() {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [myQueueCount, setMyQueueCount] = useState(0);
  const [openCount, setOpenCount] = useState(0);

  // Poll the lightweight my-queue endpoint every 60s so the badge stays fresh
  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const res = await api.get('/tasks/my-queue');
        if (alive) {
          setMyQueueCount(res.data?.count || 0);
          setOpenCount(res.data?.open_count || 0);
        }
      } catch { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    // Also refresh when a WS notification arrives (status change / assignment)
    const onWs = () => fetchCount();
    window.addEventListener('ws-notification', onWs);
    return () => { alive = false; clearInterval(id); window.removeEventListener('ws-notification', onWs); };
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error('New passwords do not match'); return; }
    if (newPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.put('/auth/change-password', { old_password: oldPwd, new_password: newPwd });
      toast.success('Password changed successfully');
      setPwdDialogOpen(false); setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to change password'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('reminderSnoozedUntil');
    window.location.href = '/login';
  };

  const roleDisplayNames = { super_admin: 'SUPER ADMIN', admin: 'ADMIN', member: 'MEMBER' };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'admin'] },
    { path: '/', icon: CheckSquare, label: 'My Work', roles: ['member'], badge: openCount },
    { path: '/tasks?queue=mine', icon: Inbox, label: 'My Queue', roles: ['super_admin', 'admin', 'member'], badge: myQueueCount },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks', roles: ['super_admin', 'admin'], badge: openCount },
    { path: '/clients', icon: Users, label: 'Clients', roles: ['super_admin', 'admin'] },
    { path: '/services', icon: Wrench, label: 'Services', roles: ['super_admin', 'admin'] },
    { path: '/activity-log', icon: Activity, label: 'Activity Log', roles: ['super_admin', 'admin'] },
    { path: '/team-efficiency', icon: BarChart3, label: 'Efficiency', roles: ['super_admin'] },
    { path: '/transfer', icon: ArrowLeftRight, label: 'Transfer', roles: ['super_admin'] },
    { path: '/timesheet', icon: FileSpreadsheet, label: 'Timesheet', roles: ['super_admin', 'admin'] },
    { path: '/holidays', icon: CalendarDays, label: 'Holidays', roles: ['super_admin', 'admin'] },
    { path: '/team', icon: UserCheck, label: 'Team', roles: ['super_admin'] },
    { path: '/team-hours', icon: Clock, label: 'Team Hours', roles: ['super_admin'] },
    { path: '/attendance', icon: CalendarDays, label: 'Attendance', roles: ['super_admin'] },
    { path: '/hr', icon: BriefcaseBusiness, label: 'HR Master', roles: ['super_admin'] },
    { path: '/my-profile', icon: FolderOpen, label: 'My Profile', roles: ['super_admin', 'admin', 'member'] },
    { path: '/shortcuts', icon: Keyboard, label: 'Shortcuts', roles: ['super_admin', 'admin', 'member'] },
    { path: '/leave', icon: CalendarDays, label: 'Leaves', roles: ['super_admin', 'admin', 'member'] },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // My Queue link includes a query string; match exactly
    if (path.includes('?')) {
      const [p, q] = path.split('?');
      return location.pathname === p && location.search.includes(q);
    }
    // For /tasks, don't match when on /tasks?queue=mine (that's My Queue)
    if (path === '/tasks') {
      return location.pathname.startsWith(path) && !location.search.includes('queue=mine');
    }
    return location.pathname.startsWith(path);
  };

  const visibleItems = [];
  const seenPaths = new Set();
  for (const item of menuItems) {
    if (!item.roles.includes(user.role)) continue;
    if (seenPaths.has(item.path)) continue;
    seenPaths.add(item.path);
    visibleItems.push(item);
  }

  const sidebarContent = (
    <>
      <div className="p-4 md:p-6 border-b border-white/10">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="h-10 w-10 md:h-14 md:w-14 bg-white rounded-lg p-1.5 md:p-2 flex items-center justify-center flex-shrink-0 shadow-lg">
            <img src="/company-logo.jpg" alt="Company Logo" className="h-full w-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-white">FinTrack Pro</h1>
            <p className="text-xs text-emerald-400/80 uppercase tracking-wide font-medium">
              {roleDisplayNames[user.role] || user.role?.toUpperCase()}
            </p>
          </div>
          <button onClick={() => setMobileOpen(false)} className="md:hidden p-1 text-white/70 hover:text-white" data-testid="close-sidebar">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto min-h-0">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const badgeCount = item.badge || 0;
          return (
            <Link key={item.path + item.label} to={item.path} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                active ? 'bg-gradient-to-r from-[#5B6FD6] to-[#10B981] text-white shadow-lg' : 'text-slate-300 hover:bg-white/10 hover:text-white'
              }`}>
              <Icon className={`h-5 w-5 ${active ? 'text-white' : ''}`} />
              <span className="font-medium flex-1">{item.label}</span>
              {badgeCount > 0 && (
                <span
                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[22px] text-center ${
                    active
                      ? 'bg-white/25 text-white'
                      : item.label === 'My Queue'
                        ? 'bg-purple-500 text-white'
                        : 'bg-emerald-500 text-white'
                  }`}
                  data-testid={`sidebar-badge-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 md:p-4 border-t border-white/10">
        <div className="mb-3 px-3 md:px-4 py-2.5 md:py-3 bg-white/5 rounded-lg backdrop-blur-sm flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{user.full_name}</p>
            <p className="text-xs text-emerald-400/70">{user.mobile_number}</p>
          </div>
          <NotificationBell />
        </div>
        <Button onClick={() => setPwdDialogOpen(true)} variant="ghost" data-testid="change-password-button"
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10 mb-1">
          <KeyRound className="h-5 w-5 mr-3" /> Change Password
        </Button>
        <Button onClick={handleLogout} variant="ghost" data-testid="logout-button"
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10">
          <LogOut className="h-5 w-5 mr-3" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
        className="md:hidden fixed top-3 left-3 z-50 p-2 bg-slate-900 text-white rounded-lg shadow-lg">
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} data-testid="mobile-overlay" />
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex w-64 h-screen fixed left-0 top-0 flex-col z-30" style={{
        background: 'linear-gradient(180deg, #1e1b4b 0%, #0f172a 50%, #042f2e 100%)'
      }}>
        {sidebarContent}
      </div>

      {/* Mobile sidebar */}
      <div className={`md:hidden fixed inset-y-0 left-0 w-72 z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #1e1b4b 0%, #0f172a 50%, #042f2e 100%)' }}>
        {sidebarContent}
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription className="sr-only">Change your password</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} required data-testid="old-password-input" placeholder="Enter current password" />
            </div>
            <div>
              <Label>New Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required data-testid="new-password-input" placeholder="Enter new password (min 6 chars)" />
            </div>
            <div>
              <Label>Confirm New Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required data-testid="confirm-password-input" placeholder="Re-enter new password" />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" data-testid="submit-change-password">Change Password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
